// Pure helpers for validating / normalizing the Plotly payloads returned by the
// graph pipeline before we hand them to Plotly.newPlot. Kept dependency-free so
// they can be unit tested in isolation and shared across the bundled and legacy
// (static/js/datadisplay.js) front ends.
//
// The graph pipeline emits several response shapes:
//   - an array of trace objects (the common case)
//   - a single trace object (some direct-override / LLM paths)
//   - a JSON-encoded string of either of the above (double-serialized payloads)
//   - Plotly 3.x binary arrays ({bdata, dtype}) nested inside trace fields
// We accept all of them so a valid chart never falls through to the generic
// "did not include Plotly traces" error.

// Keys that identify a value as a renderable Plotly trace rather than a raw data
// row. This must stay comprehensive across chart types: a missing key here means
// a legitimate chart type is rejected as "rows instead of traces". Note that
// serialized Plotly figures almost always carry `type`, so this list mainly
// guards hand-built / LLM-authored traces that omit it.
export const RENDERABLE_TRACE_KEYS = [
  'type',
  // cartesian / 3d / heatmap / contour / surface / histogram2d
  'x',
  'y',
  'z',
  // polar
  'r',
  'theta',
  // ternary
  'a',
  'b',
  'c',
  // pie / sunburst / treemap / icicle / funnelarea
  'values',
  'labels',
  'parents',
  'ids',
  // table
  'cells',
  'header',
  // geo / choropleth / maps
  'locations',
  'geojson',
  'lat',
  'lon',
  // financial
  'open',
  'high',
  'low',
  'close',
  // sankey
  'node',
  'link',
  'nodes',
  'links',
  // parallel coordinates / categories / splom
  'dimensions',
  // indicator (gauge / number / delta)
  'value',
  'gauge',
  'number',
  'delta',
  // image
  'source'
];

export function isRenderableTrace(trace) {
  if (!trace || typeof trace !== 'object' || Array.isArray(trace)) {
    return false;
  }
  return RENDERABLE_TRACE_KEYS.some(key => trace[key] !== undefined);
}

// Best-effort coercion of whatever lands in the `data` field into an array of
// traces. Returns null when the value cannot represent traces at all (so callers
// can show a clear error), or an array (possibly empty) otherwise.
export function coercePlotlyTraces(data) {
  if (Array.isArray(data)) {
    return data;
  }
  if (typeof data === 'string') {
    const trimmed = data.trim();
    if (!trimmed) {
      return null;
    }
    try {
      const parsed = JSON.parse(trimmed);
      return coercePlotlyTraces(parsed);
    } catch (_error) {
      // Not JSON - fall through to the null return below.
    }
    return null;
  }
  if (isRenderableTrace(data)) {
    return [data];
  }
  // A nested Plotly figure object ({data: [...traces], layout: {...}}). Some
  // pipeline paths (multi-figure merges, double-wrapped LLM payloads) hand us the
  // whole figure where a bare trace array is expected; unwrap it rather than
  // falling through to the generic "did not include Plotly traces" error.
  if (data && typeof data === 'object' && Array.isArray(data.data)) {
    return data.data;
  }
  return null;
}

export function getPlotlyPayloadIssue(data) {
  const traces = coercePlotlyTraces(data);
  if (!traces) {
    return 'The graph response did not include Plotly traces.';
  }
  if (traces.length === 0) {
    return 'The graph response did not include any traces.';
  }
  const hasRenderableTrace = traces.some(isRenderableTrace);
  return hasRenderableTrace
    ? ''
    : 'The graph response returned rows instead of renderable Plotly traces.';
}

// A single graph frame from a multi-graph (`graphs: [...]`) response is only
// renderable as a Plotly chart if its traces coerce to at least one renderable
// trace. The pipeline can emit extra "analysis" frames at depth+1 that carry no
// data; rendered as-is, each one produces its own "did not include Plotly
// traces" error box. Frames may hold their traces directly or under `.data`.
export function isRenderableGraphFrame(frame) {
  if (!frame || typeof frame !== 'object') {
    return false;
  }
  const candidate = (frame.data !== undefined) ? frame.data : frame;
  return getPlotlyPayloadIssue(candidate) === '';
}

// Keep only the frames that will actually render. Returns a new array; non-array
// input yields []. Callers should only substitute the filtered list when it is
// non-empty, so a fully-dataless response still surfaces its error as before.
export function filterRenderableGraphFrames(graphs) {
  if (!Array.isArray(graphs)) {
    return [];
  }
  return graphs.filter(isRenderableGraphFrame);
}

export function isPlotlyTablePayload(plotlyJSON) {
  return Boolean(
    plotlyJSON &&
    Array.isArray(plotlyJSON.data) &&
    plotlyJSON.data.length > 0 &&
    plotlyJSON.data[0] &&
    plotlyJSON.data[0].type === 'table' &&
    plotlyJSON.data[0].cells
  );
}

export function decodePlotlyBinaryArray(value) {
  if (!value || typeof value !== 'object' || !value.bdata || !value.dtype) {
    return value;
  }
  const dtype = String(value.dtype).replace(/[<>=|]/g, '');
  const constructors = {
    i1: Int8Array,
    u1: Uint8Array,
    i2: Int16Array,
    u2: Uint16Array,
    i4: Int32Array,
    u4: Uint32Array,
    f4: Float32Array,
    f8: Float64Array
  };
  const TypedArray = constructors[dtype];
  if (!TypedArray || typeof atob !== 'function') {
    return value;
  }
  try {
    const binary = atob(value.bdata);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return Array.from(new TypedArray(bytes.buffer));
  } catch (error) {
    console.warn('Could not decode Plotly binary array payload:', error);
    return value;
  }
}

export function normalizePlotlyBinaryArrays(value) {
  const decoded = decodePlotlyBinaryArray(value);
  if (decoded !== value) {
    return decoded;
  }
  if (Array.isArray(value)) {
    return value.map(normalizePlotlyBinaryArrays);
  }
  if (!value || typeof value !== 'object') {
    return value;
  }
  const normalized = {};
  Object.keys(value).forEach(key => {
    normalized[key] = normalizePlotlyBinaryArrays(value[key]);
  });
  return normalized;
}

function isString(value) {
  return typeof value === 'string' || value instanceof String;
}

function looksLikeSvgImageSource(value) {
  if (!isString(value)) return false;
  const src = String(value).trim();
  if (!src) return false;
  const lower = src.toLowerCase();

  if (lower.startsWith('data:image/svg') || lower.startsWith('data:image/svg+xml')) {
    return true;
  }
  if (lower.includes('image/svg+xml')) {
    return true;
  }
  // Some LLM paths accidentally return raw SVG markup rather than a URL/data URI.
  if (lower.startsWith('<svg') || lower.includes('<svg')) {
    return true;
  }

  const withoutQuery = lower.split(/[?#]/, 1)[0];
  return withoutQuery.endsWith('.svg');
}

function looksLikeNonRasterDataUri(value) {
  if (!isString(value)) return false;
  const src = String(value).trim().toLowerCase();
  if (!src.startsWith('data:image/')) return false;
  return !(
    src.startsWith('data:image/png') ||
    src.startsWith('data:image/jpeg') ||
    src.startsWith('data:image/jpg')
  );
}

// Plotly's image trace/layout.images do not support SVG sources and can emit
// noisy console errors when the figure contains them. Because the graph pipeline
// can emit arbitrary user/LLM-authored payloads, strip out unsupported sources
// before handing the figure to Plotly.
//
// Returns possibly-updated `{ traces, layout }` so callers can pass the safe
// copies to Plotly.newPlot. The function mutates `layout.images` when needed.
export function stripUnsupportedPlotlyImages(traces, layout) {
  const result = {
    traces,
    layout,
    removedLayoutImages: 0,
    removedImageTraces: 0,
  };

  if (Array.isArray(traces)) {
    const filteredTraces = traces.filter(trace => {
      if (!trace || typeof trace !== 'object') return true;
      const src = trace.source;
      if (!isString(src)) return true;
      if (!looksLikeSvgImageSource(src) && !looksLikeNonRasterDataUri(src)) return true;
      result.removedImageTraces += 1;
      return false;
    });
    if (filteredTraces.length !== traces.length) {
      result.traces = filteredTraces;
    }
  }

  if (layout && typeof layout === 'object' && !Array.isArray(layout)) {
    const images = layout.images;
    if (Array.isArray(images)) {
      const filteredImages = images.filter(img => {
        if (!img || typeof img !== 'object') return true;
        const src = img.source;
        if (!isString(src)) return true;
        if (!looksLikeSvgImageSource(src) && !looksLikeNonRasterDataUri(src)) return true;
        result.removedLayoutImages += 1;
        return false;
      });
      if (filteredImages.length !== images.length) {
        layout.images = filteredImages;
      }
    }
  }

  return result;
}
